"use client";

import Image from "next/image";
import { useSection } from "./_hooks/useSection";
import { useRouter } from "next/navigation";
import ConvertSkeleton from "./_skeletons/ConvertSkeleton";
import { frontendImage } from "@/lib/helpers";
import { useEffect, useRef, useState } from "react";
import useConvertCalculation from "@/app/_hooks/useConvertCalculation";
import { useConvertContext } from "./ConvertContext";

// Currency formatting function
const formatCurrency = (value) => {
  if (!value || value === "") return "";
  const numValue = parseFloat(value);
  if (isNaN(numValue)) return value;
  return numValue.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

// Parse formatted currency back to number
const parseCurrency = (value) => {
  if (!value) return "";
  return value.replace(/,/g, "");
};

export const Convert = () => {
  const router = useRouter();
  const { content, loading: sectionLoading } = useSection("convert");

  const {
    loading,
    fromOpen,
    toOpen,
    senderCountries,
    receiverCountries,
    fromSelected,
    toSelected,
    amount,
    recipientAmount,
    fees,
    conversionRate,
    totalYouPay,
    selectedFacility,
    setFromOpen,
    setToOpen,
    setFromSelected,
    setToSelected,
    setAmount,
    setRecipientAmount,
    setLastEdited,
    setSelectedFacility,
  } = useConvertCalculation();

  const { toSelected: contextToSelected, setToSelected: setContextToSelected } =
    useConvertContext();

  const senderRef = useRef(null);
  const recipientRef = useRef(null);
  const transactionTypeRef = useRef(null);

  const [senderSearch, setSenderSearch] = useState("");
  const [recipientSearch, setRecipientSearch] = useState("");
  const [transactionTypeOpen, setTransactionTypeOpen] = useState(false);
  const [senderInputFocused, setSenderInputFocused] = useState(false);
  const [recipientInputFocused, setRecipientInputFocused] = useState(false);

  // Get available facilities from selected receiver country
  const availableFacilities = toSelected?.facilities || [];

  // Set default transaction type when receiver country changes
  useEffect(() => {
    if (availableFacilities.length > 0) {
      const isCurrentSelectionValid = availableFacilities.some(
        (facility) => facility.id === selectedFacility?.id,
      );

      if (!isCurrentSelectionValid) {
        setSelectedFacility(null);
      }
    } else {
      setSelectedFacility(null);
    }
  }, [availableFacilities, selectedFacility]);

  // Reset transaction type when receiver country changes
  useEffect(() => {
    setSelectedFacility(null);
  }, [toSelected]);

  // Sync local toSelected to context for Banner
  useEffect(() => {
    if (toSelected && toSelected !== contextToSelected) {
      setContextToSelected(toSelected);
    }
  }, [toSelected, contextToSelected, setContextToSelected]);

  const toggleSenderDropdown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setFromOpen((prev) => !prev);
    setToOpen(false);
    setTransactionTypeOpen(false);

    // Clear recipient country and search when sender dropdown is opened
    setToSelected(null);
    setRecipientSearch("");
  };

  const toggleRecipientDropdown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setToOpen((prev) => !prev);
    setFromOpen(false);
    setTransactionTypeOpen(false);
  };

  const toggleTransactionTypeDropdown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setTransactionTypeOpen((prev) => !prev);
    setFromOpen(false);
    setToOpen(false);
  };

  const closeDropdowns = () => {
    setFromOpen(false);
    setToOpen(false);
    setTransactionTypeOpen(false);
  };

  const handleClickOutside = (e) => {
    if (
      senderRef.current &&
      !senderRef.current.contains(e.target) &&
      recipientRef.current &&
      !recipientRef.current.contains(e.target) &&
      transactionTypeRef.current &&
      !transactionTypeRef.current.contains(e.target)
    ) {
      closeDropdowns();
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  if (sectionLoading || loading) return <ConvertSkeleton />;

  const filteredSenderCountries = senderCountries.filter(
    (item) =>
      item.name.toLowerCase().includes(senderSearch.toLowerCase()) &&
      item.code !== toSelected?.code,
  );

  let filteredRecipientCountries = receiverCountries.filter(
    (item) =>
      item.name.toLowerCase().includes(recipientSearch.toLowerCase()) &&
      item.code !== fromSelected?.code,
  );

  // Only filter if a sender country is selected and has conversion_rates
  if (fromSelected?.conversion_rates?.length) {
    const allowedIds = fromSelected.conversion_rates.map((rate) => rate.to);
    filteredRecipientCountries = filteredRecipientCountries.filter((country) =>
      allowedIds.includes(country.id),
    );
  }

  return (
    <>
      <section
        className="global-money-section section-bg pt-100"
        style={{ position: "relative", zIndex: "20 !important" }}
      >
        <div class="gradient-border-wrapper">
          <div className="global-money-wrapper">
            {/* Form card */}
            <div style={{ width: "100%" }}>
              <div className="global-money-card" style={{ width: "100%" }}>
                <div
                  className="global-money-card__content"
                  style={{ position: "relative", zIndex: 4 }}
                >
                  {/* Conversion rate */}
                  <div className="global-money-card__calculation">
                    {fromSelected?.code === "NGN"
                      ? `${(1 / conversionRate).toFixed(4)} ${fromSelected?.code} = 1 ${toSelected?.code}`
                      : `1 ${fromSelected?.code} = ${conversionRate} ${toSelected?.code}`}
                  </div>
                  <form>
                    {/* You Send */}
                    <div className="form-group mb-0" style={{ width: "100%" }}>
                      <label className="form--label">You Send</label>
                      <div
                        className={`has-currency-dropdown ${toOpen ? "off" : ""}`}
                      >
                        <input
                          type="text"
                          className="form--control"
                          placeholder="Enter Amount"
                          value={
                            senderInputFocused ? amount : formatCurrency(amount)
                          }
                          onChange={(e) => {
                            const rawValue = parseCurrency(e.target.value);
                            setAmount(rawValue);
                            setLastEdited("amount");
                          }}
                          onFocus={() => setSenderInputFocused(true)}
                          onBlur={() => setSenderInputFocused(false)}
                        />
                        <div
                          ref={senderRef}
                          className={`currency-dropdown ${fromOpen ? "open" : ""}`}
                          onClick={toggleSenderDropdown}
                        >
                          <div className="currency-dropdown__selected">
                            <span className="thumb">
                              <Image
                                src={
                                  fromSelected?.get_image || fromSelected?.image
                                }
                                alt={fromSelected?.name}
                                width={20}
                                height={20}
                              />
                            </span>
                            <div className="content">
                              <span className="full-form">
                                {fromSelected?.name}
                              </span>
                              <span className="short-form">
                                {fromSelected?.code}
                              </span>
                            </div>
                          </div>

                          {fromOpen && (
                            <ul
                              className="currency-dropdown__list"
                              style={{
                                scrollbarWidth: "thin",
                                scrollbarColor: "#c1c1c1 #f1f1f1",
                                height: "150px",
                                overflowY: "scroll",
                              }}
                            >
                              <li className="currency-dropdown__list-search">
                                <div className="currency-dropdown__list-search-form">
                                  <div className="form-group1 position-relative mb-0">
                                    <input
                                      type="text"
                                      className="form--control"
                                      placeholder="Search"
                                      value={senderSearch}
                                      onClick={(e) => e.stopPropagation()}
                                      onChange={(e) =>
                                        setSenderSearch(e.target.value)
                                      }
                                    />
                                    <button type="submit" className="off">
                                      <span className="icon">
                                        <svg
                                          xmlns="http://www.w3.org/2000/svg"
                                          width=""
                                          height=""
                                          viewBox="0 0 20 20"
                                          fill="none"
                                        >
                                          <path
                                            d="M9.58366 18.1248C4.87533 18.1248 1.04199 14.2915 1.04199 9.58317C1.04199 4.87484 4.87533 1.0415 9.58366 1.0415C14.292 1.0415 18.1253 4.87484 18.1253 9.58317C18.1253 14.2915 14.292 18.1248 9.58366 18.1248ZM9.58366 2.2915C5.55866 2.2915 2.29199 5.5665 2.29199 9.58317C2.29199 13.5998 5.55866 16.8748 9.58366 16.8748C13.6087 16.8748 16.8753 13.5998 16.8753 9.58317C16.8753 5.5665 13.6087 2.2915 9.58366 2.2915Z"
                                            fill="currentColor"
                                          ></path>
                                          <path
                                            d="M18.3335 18.9585C18.1752 18.9585 18.0169 18.9002 17.8919 18.7752L16.2252 17.1085C15.9835 16.8669 15.9835 16.4669 16.2252 16.2252C16.4669 15.9835 16.8669 15.9835 17.1085 16.2252L18.7752 17.8919C19.0169 18.1335 19.0169 18.5335 18.7752 18.7752C18.6502 18.9002 18.4919 18.9585 18.3335 18.9585Z"
                                            fill="currentColor"
                                          ></path>
                                        </svg>
                                      </span>
                                    </button>
                                  </div>
                                </div>
                              </li>

                              {filteredSenderCountries.map((item, idx) => (
                                <li
                                  key={idx}
                                  className="currency-dropdown__list-item"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setFromSelected(item);
                                    closeDropdowns();
                                  }}
                                >
                                  <span className="thumb">
                                    <Image
                                      src={item.get_image || item.image}
                                      alt={item.name}
                                      width={20}
                                      height={20}
                                    />
                                  </span>
                                  <div className="content">
                                    <span className="full-form">
                                      {item.name}
                                    </span>
                                    <span className="short-form">
                                      {item.code}
                                    </span>
                                  </div>
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Recipient Gets */}
                    <div className="form-group1" style={{ width: "100%" }}>
                      <label className="form--label">Recipient Gets</label>
                      <div
                        className={`has-currency-dropdown ${fromOpen ? "off" : ""}`}
                      >
                        <input
                          type="text"
                          className="form--control"
                          placeholder="Enter Amount"
                          value={
                            recipientInputFocused
                              ? recipientAmount
                              : formatCurrency(recipientAmount)
                          }
                          onChange={(e) => {
                            const rawValue = parseCurrency(e.target.value);
                            setRecipientAmount(rawValue);
                            setLastEdited("recipientAmount");
                          }}
                          onFocus={() => setRecipientInputFocused(true)}
                          onBlur={() => setRecipientInputFocused(false)}
                        />
                        <div
                          ref={recipientRef}
                          className={`currency-dropdown ${toOpen ? "open" : ""}`}
                          onClick={toggleRecipientDropdown}
                        >
                          <div className="currency-dropdown__selected">
                            <span className="thumb">
                              <Image
                                src={toSelected?.get_image || toSelected?.image}
                                alt={toSelected?.name}
                                width={20}
                                height={20}
                              />
                            </span>
                            <div className="content">
                              <span className="full-form">
                                {toSelected?.name}
                              </span>
                              <span className="short-form">
                                {toSelected?.code}
                              </span>
                            </div>
                          </div>

                          {toOpen && (
                            <ul
                              className="currency-dropdown__list"
                              style={{
                                scrollbarWidth: "thin",
                                scrollbarColor: "#c1c1c1 #f1f1f1",
                                height: "150px",
                                overflowY: "scroll",
                              }}
                            >
                              <li className="currency-dropdown__list-search">
                                <div className="currency-dropdown__list-search-form">
                                  <div className="form-group1 position-relative mb-0">
                                    <input
                                      type="text"
                                      className="form--control"
                                      placeholder="Search"
                                      value={recipientSearch}
                                      onClick={(e) => e.stopPropagation()}
                                      onChange={(e) =>
                                        setRecipientSearch(e.target.value)
                                      }
                                    />

                                    <button type="submit" className="off">
                                      <span className="icon">
                                        <svg
                                          xmlns="http://www.w3.org/2000/svg"
                                          width=""
                                          height=""
                                          viewBox="0 0 20 20"
                                          fill="none"
                                        >
                                          <path
                                            d="M9.58366 18.1248C4.87533 18.1248 1.04199 14.2915 1.04199 9.58317C1.04199 4.87484 4.87533 1.0415 9.58366 1.0415C14.292 1.0415 18.1253 4.87484 18.1253 9.58317C18.1253 14.2915 14.292 18.1248 9.58366 18.1248ZM9.58366 2.2915C5.55866 2.2915 2.29199 5.5665 2.29199 9.58317C2.29199 13.5998 5.55866 16.8748 9.58366 16.8748C13.6087 16.8748 16.8753 13.5998 16.8753 9.58317C16.8753 5.5665 13.6087 2.2915 9.58366 2.2915Z"
                                            fill="currentColor"
                                          ></path>
                                          <path
                                            d="M18.3335 18.9585C18.1752 18.9585 18.0169 18.9002 17.8919 18.7752L16.2252 17.1085C15.9835 16.8669 15.9835 16.4669 16.2252 16.2252C16.4669 15.9835 16.8669 15.9835 17.1085 16.2252L18.7752 17.8919C19.0169 18.1335 19.0169 18.5335 18.7752 18.7752C18.6502 18.9002 18.4919 18.9585 18.3335 18.9585Z"
                                            fill="currentColor"
                                          ></path>
                                        </svg>
                                      </span>
                                    </button>
                                  </div>
                                </div>
                              </li>
                              {filteredRecipientCountries.map((item, idx) => (
                                <li
                                  key={idx}
                                  className="currency-dropdown__list-item"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setToSelected(item);
                                    closeDropdowns();
                                  }}
                                >
                                  <span className="thumb">
                                    <Image
                                      src={item.get_image || item.image}
                                      alt={item.name}
                                      width={20}
                                      height={20}
                                    />
                                  </span>
                                  <div className="content">
                                    <span className="full-form">
                                      {item.name}
                                    </span>
                                    <span className="short-form">
                                      {item.code}
                                    </span>
                                  </div>
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      </div>
                    </div>
                    {/* Transaction Type */}
                    <div style={{ width: "100%" }}>
                      <label className="form--label">Transaction Type</label>
                      <div
                        ref={transactionTypeRef}
                        onClick={(e) => {
                          // Only allow opening if amount and both countries are selected
                          if (amount && fromSelected && toSelected) {
                            toggleTransactionTypeDropdown(e);
                          }
                        }}
                        style={{
                          width: "100%",
                          border: "1px solid #e1e5e9",
                          borderRadius: "4px",
                          backgroundColor:
                            amount && fromSelected && toSelected
                              ? "#E9E9E9"
                              : "#f5f5f5",
                          cursor:
                            amount && fromSelected && toSelected
                              ? "pointer"
                              : "not-allowed",
                          position: "relative",
                          minHeight: "30px",
                          opacity:
                            amount && fromSelected && toSelected ? 1 : 0.7,
                        }}
                      >
                        <div
                          style={{
                            padding: "12px 16px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            minHeight: "50px",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              flex: 1,
                            }}
                          >
                            {selectedFacility?.svg_image && (
                              <div
                                style={{
                                  marginRight: "12px",
                                  display: "flex",
                                  alignItems: "center",
                                  width: "20px",
                                  height: "20px",
                                }}
                                dangerouslySetInnerHTML={{
                                  __html: selectedFacility.svg_image,
                                }}
                              />
                            )}

                            <div style={{ flex: 1 }}>
                              <div
                                style={{
                                  fontWeight: "500",
                                  color: selectedFacility ? "#333" : "#999",
                                  fontSize: "14px",
                                  lineHeight: "1.4",
                                }}
                              >
                                {selectedFacility?.name || "Please select"}
                              </div>
                            </div>
                          </div>

                          <div
                            style={{
                              transform: transactionTypeOpen
                                ? "rotate(180deg)"
                                : "rotate(0deg)",
                              transition: "transform 0.2s ease",
                              color: "#666",
                              marginLeft: "8px",
                              opacity:
                                amount && fromSelected && toSelected ? 1 : 0.5,
                            }}
                          >
                            <svg
                              width="12"
                              height="8"
                              viewBox="0 0 12 8"
                              fill="none"
                            >
                              <path
                                d="M1 1L6 6L11 1"
                                stroke="currentColor"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </div>
                        </div>

                        {transactionTypeOpen &&
                          amount &&
                          fromSelected &&
                          toSelected && (
                            <div
                              style={{
                                position: "absolute",
                                top: "100%",
                                left: "0",
                                right: "0",
                                backgroundColor: "#fff",
                                border: "1px solid #e1e5e9",
                                borderRadius: "8px",
                                marginTop: "4px",
                                maxHeight: "200px",
                                overflowY: "auto",
                                zIndex: 1000,
                                boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                              }}
                            >
                              {availableFacilities.length === 0 ? (
                                <div
                                  style={{
                                    padding: "12px 16px",
                                    color: "#999",
                                    textAlign: "center",
                                    fontSize: "14px",
                                  }}
                                >
                                  No transaction types available
                                </div>
                              ) : (
                                availableFacilities.map((facility, idx) => (
                                  <div
                                    key={facility.id || idx}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setSelectedFacility(facility);
                                      closeDropdowns();
                                    }}
                                    style={{
                                      padding: "12px 16px",
                                      cursor: "pointer",
                                      display: "flex",
                                      alignItems: "center",
                                      borderBottom:
                                        idx < availableFacilities.length - 1
                                          ? "1px solid #f0f0f0"
                                          : "none",
                                      transition: "background-color 0.2s ease",
                                      backgroundColor:
                                        selectedFacility?.id === facility.id
                                          ? "#f8f9fa"
                                          : "transparent",
                                      minHeight: "44px",
                                    }}
                                    onMouseEnter={(e) => {
                                      e.currentTarget.style.backgroundColor =
                                        "#f8f9fa";
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.backgroundColor =
                                        selectedFacility?.id === facility.id
                                          ? "#f8f9fa"
                                          : "transparent";
                                    }}
                                  >
                                    {facility.svg_image && (
                                      <div
                                        style={{
                                          marginRight: "12px",
                                          display: "flex",
                                          alignItems: "center",
                                          width: "20px",
                                          height: "20px",
                                        }}
                                        dangerouslySetInnerHTML={{
                                          __html: facility.svg_image,
                                        }}
                                      />
                                    )}
                                    <div style={{ flex: 1 }}>
                                      <div
                                        style={{
                                          fontWeight: "500",
                                          color: "#333",
                                          fontSize: "14px",
                                          lineHeight: "1.4",
                                        }}
                                      >
                                        {facility.name}
                                      </div>
                                    </div>
                                  </div>
                                ))
                              )}
                            </div>
                          )}
                      </div>
                    </div>
                  </form>
                  {/* Fees and Total Pay - Only show if transaction type is selected */}
                  <div className="global-money-card-fees">
                    <ul className="global-money-card__list">
                      <li className="global-money-card__list-item">
                        <span>Fees</span>
                        <span>
                          {fees !== null
                            ? `${formatCurrency(fees)} ${fromSelected?.code}`
                            : "0.00"}
                        </span>
                      </li>
                      {/* <li className="global-money-card__list-item">
                      <span>Amount we'll send</span>
                      <span>
                        {recipientAmount && !isNaN(recipientAmount)
                          ? parseFloat(recipientAmount).toFixed(2)
                          : "0.00"}{" "}
                        {toSelected?.code}
                      </span>
                    </li> */}
                      <li className="global-money-card__list-item">
                        <span>Total to Pay</span>
                        <span>
                          {!isNaN(totalYouPay)
                            ? `${formatCurrency(totalYouPay)} ${fromSelected?.code}`
                            : "0.00"}
                        </span>
                      </li>
                    </ul>

                    <button
                      type="button"
                      className="btn btn--base convert--button"
                      style={{ borderRadius: "8px", fontSize: "14px" }}
                      onClick={() => {
                        if (!amount || !recipientAmount) {
                          router.push("/user/send-money/step-1");
                        } else {
                          const query = new URLSearchParams({
                            amount: amount,
                            recipient_amount: recipientAmount,
                            sender_country_id: fromSelected?.id,
                            receiver_country_id: toSelected?.id,
                            transaction_type_id: selectedFacility?.id || "",
                          }).toString();

                          router.push(`/user/send-money/step-1?${query}`);
                        }
                      }}
                    >
                      Send Money
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};
